import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Briefcase, IndianRupee, User, Building2, Phone, Mail,
  FileText, MessageSquare, CheckCircle, Clock, Upload, Trash2, Plus,
  Send, Trophy, XCircle, Pause, Pencil, Calendar, Target, Zap,
  ChevronRight, ExternalLink, RefreshCw
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from "../components/ui/dialog";
import Layout from "../components/Layout";
import { API } from "../App";
import { toast } from "sonner";
import { usePermissions } from "../hooks/usePermissions";

const formatINR = (v) => {
  if (!v) return "0";
  const n = Number(v);
  if (n >= 10000000) return `${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `${(n / 100000).toFixed(1)} L`;
  return n.toLocaleString("en-IN");
};

const STATUS_STYLES = {
  active: "bg-blue-100 text-blue-700",
  won: "bg-emerald-100 text-emerald-700",
  lost: "bg-red-100 text-red-700",
  stalled: "bg-amber-100 text-amber-700",
};

const TABS = [
  { id: "overview", label: "Overview", icon: Briefcase },
  { id: "timeline", label: "Timeline", icon: Clock },
  { id: "negotiation", label: "Negotiation", icon: IndianRupee },
  { id: "documents", label: "Documents", icon: FileText },
  { id: "milestones", label: "Milestones", icon: CheckCircle },
  { id: "notes", label: "Notes", icon: MessageSquare },
];

const DealRoomPage = () => {
  const { dealRoomId } = useParams();
  const navigate = useNavigate();
  const { canEdit } = usePermissions();
  const fileInputRef = useRef(null);

  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [noteText, setNoteText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ deal_value: 0, commission_rate: 2, status: "active" });
  const [milestoneEditId, setMilestoneEditId] = useState(null);
  const [milestoneForm, setMilestoneForm] = useState({ status: "", due_date: "", notes: "" });
  const [addMilestoneOpen, setAddMilestoneOpen] = useState(false);
  const [newMilestoneName, setNewMilestoneName] = useState("");

  const fetchDeal = async () => {
    try {
      const res = await axios.get(`${API}/deal-rooms/${dealRoomId}`);
      setDeal(res.data);
    } catch { toast.error("Failed to load deal room"); navigate("/deal-rooms"); }
    setLoading(false);
  };

  useEffect(() => { fetchDeal(); }, [dealRoomId]);

  const isOwnerOrAdmin = deal && (deal.assigned_to === deal?.lead?.assigned_to || canEdit);

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    try {
      await axios.post(`${API}/deal-rooms/${dealRoomId}/notes`, { content: noteText });
      setNoteText("");
      fetchDeal();
      toast.success("Note added");
    } catch { toast.error("Failed to add note"); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/deal-rooms/${dealRoomId}/documents`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${file.name} uploaded`);
      fetchDeal();
    } catch { toast.error("Upload failed"); }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDeleteDoc = async (docId) => {
    try {
      await axios.delete(`${API}/deal-rooms/${dealRoomId}/documents/${docId}`);
      toast.success("Document removed");
      fetchDeal();
    } catch { toast.error("Failed to remove document"); }
  };

  const handleUpdateDeal = async () => {
    try {
      await axios.put(`${API}/deal-rooms/${dealRoomId}`, editForm);
      toast.success("Deal updated");
      setEditOpen(false);
      fetchDeal();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };

  const handleUpdateMilestone = async (milestoneId) => {
    try {
      await axios.put(`${API}/deal-rooms/${dealRoomId}/milestones/${milestoneId}`, milestoneForm);
      toast.success("Milestone updated");
      setMilestoneEditId(null);
      fetchDeal();
    } catch { toast.error("Failed to update milestone"); }
  };

  const handleAddMilestone = async () => {
    if (!newMilestoneName.trim()) return;
    try {
      await axios.post(`${API}/deal-rooms/${dealRoomId}/milestones`, { name: newMilestoneName });
      toast.success("Milestone added");
      setNewMilestoneName("");
      setAddMilestoneOpen(false);
      fetchDeal();
    } catch { toast.error("Failed to add milestone"); }
  };

  if (loading) return <Layout><div className="p-6 text-center text-slate-500">Loading deal room...</div></Layout>;
  if (!deal) return null;

  const commission = (deal.deal_value || 0) * ((deal.commission_rate || 0) / 100);
  const milestonesCompleted = (deal.milestones || []).filter(m => m.status === "completed").length;
  const milestonesTotal = (deal.milestones || []).length;

  return (
    <Layout>
      <div className="p-6 space-y-6 max-w-6xl mx-auto" data-testid="deal-room-detail">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/deal-rooms")} data-testid="back-to-deals">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-slate-900 font-['Manrope']">{deal.lead_name}</h1>
                <Badge className={`${STATUS_STYLES[deal.status]} capitalize text-xs`}>{deal.status}</Badge>
              </div>
              <p className="text-sm text-slate-500">{deal.project_name || "No project"} &middot; Assigned to {deal.assigned_to_name || "—"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate(`/leads/${deal.lead_id}`)} data-testid="go-to-lead">
              <ExternalLink className="w-3.5 h-3.5 mr-1" /> Lead
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setEditForm({ deal_value: deal.deal_value, commission_rate: deal.commission_rate, status: deal.status }); setEditOpen(true); }} data-testid="edit-deal-btn">
              <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-white border-slate-200"><CardContent className="p-4">
            <p className="text-xs text-slate-500">Deal Value</p>
            <p className="text-xl font-bold text-slate-900 font-['Manrope']">₹{formatINR(deal.deal_value)}</p>
          </CardContent></Card>
          <Card className="bg-white border-slate-200"><CardContent className="p-4">
            <p className="text-xs text-slate-500">Commission ({deal.commission_rate}%)</p>
            <p className="text-xl font-bold text-emerald-600 font-['Manrope']">₹{formatINR(commission)}</p>
          </CardContent></Card>
          <Card className="bg-white border-slate-200"><CardContent className="p-4">
            <p className="text-xs text-slate-500">Milestones</p>
            <p className="text-xl font-bold text-blue-600 font-['Manrope']">{milestonesCompleted}/{milestonesTotal}</p>
          </CardContent></Card>
          <Card className="bg-white border-slate-200"><CardContent className="p-4">
            <p className="text-xs text-slate-500">Documents</p>
            <p className="text-xl font-bold text-slate-900 font-['Manrope']">{(deal.documents || []).length}</p>
          </CardContent></Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors
                ${activeTab === tab.id ? "text-slate-900 border-b-2 border-slate-900" : "text-slate-500 hover:text-slate-700"}`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="min-h-[400px]">

          {/* ─── Overview ─── */}
          {activeTab === "overview" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Lead Info */}
              <Card className="bg-white border-slate-200">
                <CardHeader><CardTitle className="text-base font-['Manrope']">Lead Information</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {deal.lead && (
                    <>
                      <div className="flex justify-between text-sm"><span className="text-slate-500">Name</span><span className="text-slate-800 font-medium">{deal.lead.name}</span></div>
                      <div className="flex justify-between text-sm"><span className="text-slate-500">Phone</span><span className="text-slate-800">{deal.lead.phone || "—"}</span></div>
                      <div className="flex justify-between text-sm"><span className="text-slate-500">Email</span><span className="text-slate-800">{deal.lead.email || "—"}</span></div>
                      <div className="flex justify-between text-sm"><span className="text-slate-500">Score</span><Badge className="capitalize">{deal.lead.score || "—"}</Badge></div>
                      <div className="flex justify-between text-sm"><span className="text-slate-500">Stage</span><Badge variant="outline" className="capitalize">{deal.lead.stage || "—"}</Badge></div>
                      <div className="flex justify-between text-sm"><span className="text-slate-500">Budget</span><span className="text-slate-800">₹{formatINR(deal.lead.budget_min)} - ₹{formatINR(deal.lead.budget_max)}</span></div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Commission Calculator */}
              <Card className="bg-white border-slate-200">
                <CardHeader><CardTitle className="text-base font-['Manrope']">Deal Calculator</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Deal Value</span><span className="text-lg font-bold text-slate-900">₹{formatINR(deal.deal_value)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Commission Rate</span><span className="text-slate-800">{deal.commission_rate}%</span></div>
                  <div className="border-t border-slate-100 pt-3 flex justify-between">
                    <span className="text-slate-700 font-medium">Estimated Commission</span>
                    <span className="text-lg font-bold text-emerald-600">₹{formatINR(commission)}</span>
                  </div>
                  {deal.negotiation && (
                    <div className="border-t border-slate-100 pt-3 space-y-2">
                      <p className="text-sm font-medium text-slate-700">Negotiation Summary</p>
                      <div className="flex justify-between text-sm"><span className="text-slate-500">Quoted</span><span>₹{formatINR(deal.negotiation.initial_quoted_price)}</span></div>
                      <div className="flex justify-between text-sm"><span className="text-slate-500">Current Offer</span><span>₹{formatINR(deal.negotiation.current_offered_price)}</span></div>
                      <div className="flex justify-between text-sm"><span className="text-slate-500">Discount</span><span className="text-amber-600">{deal.negotiation.total_discount_pct}%</span></div>
                      <div className="flex justify-between text-sm"><span className="text-slate-500">Rounds</span><span>{(deal.negotiation.rounds || []).length}</span></div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recent Site Visits */}
              <Card className="bg-white border-slate-200 md:col-span-2">
                <CardHeader><CardTitle className="text-base font-['Manrope']">Site Visits</CardTitle></CardHeader>
                <CardContent>
                  {(deal.visits || []).length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4">No site visits for this lead</p>
                  ) : (
                    <div className="space-y-2">
                      {(deal.visits || []).slice(0, 5).map(v => (
                        <div key={v.visit_id} className="flex items-center justify-between text-sm p-2 rounded bg-slate-50">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-3.5 h-3.5 text-slate-400" />
                            <span>{new Date(v.scheduled_date).toLocaleDateString()}</span>
                            {v.visit_type === "revisit" && <Badge variant="outline" className="text-xs text-fuchsia-600 border-fuchsia-200">Revisit</Badge>}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-slate-500">{v.project || "—"}</span>
                            <Badge className={`text-xs capitalize ${v.status === "completed" ? "bg-emerald-100 text-emerald-700" : v.status === "scheduled" ? "bg-blue-100 text-blue-700" : "bg-red-100 text-red-700"}`}>{v.status}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* ─── Timeline ─── */}
          {activeTab === "timeline" && (
            <Card className="bg-white border-slate-200">
              <CardHeader><CardTitle className="text-base font-['Manrope']">Activity Timeline</CardTitle></CardHeader>
              <CardContent>
                {(deal.activity_log || []).length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-8">No activity yet</p>
                ) : (
                  <div className="relative pl-6">
                    <div className="absolute left-2 top-0 bottom-0 w-px bg-slate-200" />
                    {[...(deal.activity_log || [])].reverse().map((act, i) => (
                      <div key={act.activity_id || i} className="relative mb-4 last:mb-0" data-testid={`activity-${act.activity_id}`}>
                        <div className="absolute -left-4 top-1 w-3 h-3 rounded-full bg-slate-300 border-2 border-white" />
                        <div className="text-sm">
                          <span className="font-medium text-slate-800">{act.by_name || "System"}</span>
                          <span className="text-slate-500 ml-1">{act.details}</span>
                        </div>
                        <p className="text-xs text-slate-400 mt-0.5">{new Date(act.timestamp).toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* ─── Negotiation ─── */}
          {activeTab === "negotiation" && (
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-['Manrope']">Negotiation History</CardTitle>
                  {deal.negotiation && (
                    <Button variant="outline" size="sm" onClick={() => navigate("/negotiations")} data-testid="go-to-negotiations">
                      <ExternalLink className="w-3.5 h-3.5 mr-1" /> Full View
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {!deal.negotiation ? (
                  <div className="text-center py-8">
                    <IndianRupee className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No negotiation linked yet</p>
                    <p className="text-xs text-slate-400 mt-1">Create a negotiation from the Negotiations page for this lead</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500">Quoted Price</p>
                        <p className="text-sm font-bold">₹{formatINR(deal.negotiation.initial_quoted_price)}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500">Current Offer</p>
                        <p className="text-sm font-bold text-blue-600">₹{formatINR(deal.negotiation.current_offered_price)}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500">Discount</p>
                        <p className="text-sm font-bold text-amber-600">{deal.negotiation.total_discount_pct}%</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500">Status</p>
                        <Badge className={`${STATUS_STYLES[deal.negotiation.status]} capitalize text-xs mt-0.5`}>{deal.negotiation.status}</Badge>
                      </div>
                    </div>
                    {/* Rounds */}
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-slate-700">Rounds</p>
                      {(deal.negotiation.rounds || []).map((r, i) => (
                        <div key={i} className="flex items-center justify-between text-sm p-2.5 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <span className="w-6 h-6 bg-slate-200 rounded-full flex items-center justify-center text-xs font-bold">{r.round_number}</span>
                            <span className="text-slate-700">₹{formatINR(r.offered_price)}</span>
                            {r.discount_pct > 0 && <span className="text-xs text-amber-600">-{r.discount_pct}%</span>}
                          </div>
                          <div className="flex items-center gap-2">
                            {r.freebies?.length > 0 && <span className="text-xs text-slate-400">{r.freebies.join(", ")}</span>}
                            <span className="text-xs text-slate-400">{new Date(r.timestamp).toLocaleDateString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* ─── Documents ─── */}
          {activeTab === "documents" && (
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-['Manrope']">Documents</CardTitle>
                  <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-doc-btn">
                    <Upload className="w-4 h-4 mr-1" /> {uploading ? "Uploading..." : "Upload"}
                  </Button>
                  <input ref={fileInputRef} type="file" className="hidden" onChange={handleUpload} accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" />
                </div>
              </CardHeader>
              <CardContent>
                {(deal.documents || []).length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No documents uploaded</p>
                    <p className="text-xs text-slate-400 mt-1">Upload agreements, price sheets, floor plans, etc.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(deal.documents || []).map(doc => (
                      <div key={doc.doc_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`doc-${doc.doc_id}`}>
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-slate-400" />
                          <div>
                            <p className="text-sm font-medium text-slate-800">{doc.name}</p>
                            <p className="text-xs text-slate-400">{doc.uploaded_by_name} &middot; {new Date(doc.uploaded_at).toLocaleDateString()} &middot; {(doc.size / 1024).toFixed(1)} KB</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {doc.url && (
                            <a href={doc.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">
                              Download
                            </a>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteDoc(doc.doc_id)} className="h-7 px-2 text-red-500 hover:text-red-700" data-testid={`delete-doc-${doc.doc_id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* ─── Milestones ─── */}
          {activeTab === "milestones" && (
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-['Manrope']">Payment & Booking Milestones</CardTitle>
                  <Button size="sm" variant="outline" onClick={() => setAddMilestoneOpen(true)} data-testid="add-milestone-btn">
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(deal.milestones || []).map((ms, idx) => {
                    const isEditing = milestoneEditId === ms.milestone_id;
                    return (
                      <div key={ms.milestone_id} className={`p-4 rounded-lg border ${ms.status === "completed" ? "bg-emerald-50 border-emerald-200" : ms.status === "in_progress" ? "bg-blue-50 border-blue-200" : "bg-white border-slate-200"}`} data-testid={`milestone-${ms.milestone_id}`}>
                        {isEditing ? (
                          <div className="space-y-3">
                            <p className="font-medium text-slate-800">{ms.name}</p>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs">Status</Label>
                                <Select value={milestoneForm.status} onValueChange={v => setMilestoneForm(f => ({ ...f, status: v }))}>
                                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="pending">Pending</SelectItem>
                                    <SelectItem value="in_progress">In Progress</SelectItem>
                                    <SelectItem value="completed">Completed</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label className="text-xs">Due Date</Label>
                                <Input type="date" className="h-8 text-sm" value={milestoneForm.due_date || ""} onChange={e => setMilestoneForm(f => ({ ...f, due_date: e.target.value }))} />
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs">Notes</Label>
                              <Input className="h-8 text-sm" value={milestoneForm.notes || ""} onChange={e => setMilestoneForm(f => ({ ...f, notes: e.target.value }))} />
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" className="h-7 text-xs" onClick={() => handleUpdateMilestone(ms.milestone_id)}>Save</Button>
                              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setMilestoneEditId(null)}>Cancel</Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${ms.status === "completed" ? "bg-emerald-200 text-emerald-800" : ms.status === "in_progress" ? "bg-blue-200 text-blue-800" : "bg-slate-200 text-slate-600"}`}>
                                {ms.status === "completed" ? <CheckCircle className="w-4 h-4" /> : idx + 1}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-slate-800">{ms.name}</p>
                                <div className="flex items-center gap-2 text-xs text-slate-400">
                                  {ms.due_date && <span>Due: {ms.due_date}</span>}
                                  {ms.completed_date && <span className="text-emerald-600">Done: {new Date(ms.completed_date).toLocaleDateString()}</span>}
                                  {ms.notes && <span>&middot; {ms.notes}</span>}
                                </div>
                              </div>
                            </div>
                            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => { setMilestoneEditId(ms.milestone_id); setMilestoneForm({ status: ms.status, due_date: ms.due_date || "", notes: ms.notes || "" }); }} data-testid={`edit-milestone-${ms.milestone_id}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* ─── Notes ─── */}
          {activeTab === "notes" && (
            <Card className="bg-white border-slate-200">
              <CardHeader><CardTitle className="text-base font-['Manrope']">Team Notes & Discussion</CardTitle></CardHeader>
              <CardContent>
                {/* Note input */}
                <div className="flex gap-2 mb-4">
                  <Textarea
                    placeholder="Add a note or update..."
                    value={noteText}
                    onChange={e => setNoteText(e.target.value)}
                    rows={2}
                    className="flex-1"
                    data-testid="note-input"
                  />
                  <Button onClick={handleAddNote} disabled={!noteText.trim()} className="self-end" data-testid="add-note-btn">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
                {(deal.notes || []).length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">No notes yet — start the conversation</p>
                ) : (
                  <div className="space-y-3">
                    {[...(deal.notes || [])].reverse().map(note => (
                      <div key={note.note_id} className="p-3 bg-slate-50 rounded-lg" data-testid={`note-${note.note_id}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-slate-800">{note.author_name}</span>
                          <span className="text-xs text-slate-400">{new Date(note.timestamp).toLocaleString()}</span>
                        </div>
                        <p className="text-sm text-slate-600">{note.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Edit Deal Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="sm:max-w-sm" data-testid="edit-deal-dialog">
            <DialogHeader><DialogTitle className="font-['Manrope']">Edit Deal</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Deal Value (₹)</Label>
                <Input type="number" value={editForm.deal_value} onChange={e => setEditForm(f => ({ ...f, deal_value: Number(e.target.value) }))} data-testid="edit-deal-value" />
              </div>
              <div>
                <Label>Commission Rate (%)</Label>
                <Input type="number" step="0.1" value={editForm.commission_rate} onChange={e => setEditForm(f => ({ ...f, commission_rate: Number(e.target.value) }))} data-testid="edit-commission-rate" />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={editForm.status} onValueChange={v => setEditForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger data-testid="edit-deal-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="won">Won</SelectItem>
                    <SelectItem value="lost">Lost</SelectItem>
                    <SelectItem value="stalled">Stalled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleUpdateDeal} className="w-full" data-testid="save-deal-changes-btn">Save Changes</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Milestone Dialog */}
        <Dialog open={addMilestoneOpen} onOpenChange={setAddMilestoneOpen}>
          <DialogContent className="sm:max-w-sm" data-testid="add-milestone-dialog">
            <DialogHeader><DialogTitle className="font-['Manrope']">Add Milestone</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Milestone Name</Label>
                <Input value={newMilestoneName} onChange={e => setNewMilestoneName(e.target.value)} placeholder="e.g. Loan Disbursement" data-testid="new-milestone-name" />
              </div>
              <Button onClick={handleAddMilestone} disabled={!newMilestoneName.trim()} className="w-full" data-testid="confirm-add-milestone-btn">Add Milestone</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default DealRoomPage;
